import { Injectable } from '@angular/core';
import type { VideoCoverSourceRect } from 'src/app/shared/services/video-cover-frame.util';
import { IPoint, MODEL_DOCUMENT_CATEGORY } from '../../models/user-identification';
import { IControlRectPercentage, IDocumentDetection, IFaceBboxNorm } from '../../models/ocr';
import { BoundingBox, Detection } from '@mediapipe/tasks-vision';

/** Margen al calcular el target de zoom-in (solo rampa, no clasifica zona). Ver `computeRawZoomIn`. */
export const OCR_DOCUMENT_FIT_MARGIN = 0.88;
/** Límite inferior del multiplicador de zoom extra (zoom-out máximo). */
export const OCR_EXTRA_ZOOM_MIN = 0.85;
/** Límite superior del zoom extra en móvil (documento muy lejos). */
export const OCR_EXTRA_ZOOM_MAX_MOBILE = 2.0;
/** Límite superior del zoom extra en escritorio. */
export const OCR_EXTRA_ZOOM_MAX_DESKTOP = 2.0;
/** Paso de zoom extra por frame en móvil (sube/baja de forma gradual, no a saltos). */
export const OCR_EXTRA_ZOOM_RAMP_STEP_MOBILE = 0.2;
/** Paso de zoom extra por frame en escritorio. */
export const OCR_EXTRA_ZOOM_RAMP_STEP_DESKTOP = 0.075;
/**
 * Frames consecutivos sin detección antes de empezar a bajar el zoom extra hacia 1.
 * Evita que un parpadeo del detector provoque un zoom-out brusco.
 */
export const OCR_NO_DOCUMENT_HOLD_FRAMES = 10;
/**
 * Proporción bbox/externo para el mensaje de audio «demasiado cerca» (`ocr.component` → `isTooClose`).
 * No interviene en la clasificación de zona de zoom (esa usa comparación directa con los marcos).
 */
export const OCR_TOO_CLOSE_FILL_RATIO = 0.92;

/**
 * Zona de encuadre para el zoom adaptativo OCR (NIF-485).
 *
 * - `none`: sin documento o sin datos suficientes.
 * - `too_small`: el bbox es más pequeño que el marco interno → zoom-in progresivo.
 * - `too_large`: sale del marco externo o es más grande → zoom-out.
 * - `safe`: tamaño en la banda entre marco interno y externo → congelar zoom extra.
 */
export type DocumentZoomZone = 'safe' | 'too_small' | 'too_large' | 'none';

/**
 * Geometría de encuadre OCR: marcos guía (interno/externo), validación de bordes (borde azul)
 * y zoom adaptativo del documento (NIF-485).
 *
 * Conceptos clave:
 * - **Marco externo** (`externalControlRect`): morado en UI; límite máximo del documento.
 * - **Marco interno** (`controlRectInt`): azul en UI; tamaño ideal del documento encuadrado.
 * - **BBox sensor** (`documentRectOriginal`): detección MediaPipe en píxeles del canvas de análisis.
 * - **BBox efectivo** (`validationDocumentRectOriginal`): bbox sensor escalado por zoom extra virtual.
 */
@Injectable({
	providedIn: 'root',
})
export class PositionDocumentService {
	// Draw
	private draw: boolean = true;

	// Control rects
	private externalControlRect: IPoint;
	private controlRectInt: IPoint;
	private documentRect: IPoint;
	private documentRectOriginal: IPoint; // Rectángulo original sin margen para validaciones de borde
	private documentCenter: IPoint;
	private canvasHeight: number = 0; // Altura del canvas ajustada para validaciones
	private canvasWidth: number = 0; // Anchura del canvas para validaciones
	private visibleAreaOffset: { left: number; right: number; top: number; bottom: number } = {
		left: 0,
		right: 0,
		top: 0,
		bottom: 0,
	}; // Offset del área visible basado en aspect-ratio
	private controlRectPercentage: IControlRectPercentage = {
		extHorizontally: 0.01,
		extVertically: 0.01,
		intHorizontally: 0.23,
		intVertically: 0.3,
	};
	private thresholdDistanceToEdge: number = 0.01; // 1% del canvas como threshold

	/** Proporciones de la franja barcode en scan-dibujo.svg (532×340). */
	private static readonly BARCODE_STRIP = {
		left: 20.6875 / 532,
		top: 104.688 / 340,
		width: (509.688 - 20.6875) / 532,
		height: (234.688 - 104.688) / 340,
	};

	private barcodeStripRect: IPoint | null = null;

	// Mobile
	private isMobile: boolean = false;

	// ─── Zoom adaptativo OCR (NIF-485) ───────────────────────────────────────────
	// El preview combina dos factores (ver `getEffectivePreviewZoom`):
	//   1) `getZoomFactor()` — zoom guía fijo para que el marco morado llene la pantalla (~1.02).
	//   2) `ocrExtraDocumentZoom` — multiplicador extra que este servicio ajusta frame a frame.
	//
	// Coordenadas: todo se calcula en píxeles del buffer del sensor (canvas de análisis),
	// no en píxeles CSS del vídeo en pantalla.

	/** Multiplicador extra actual (1 = sin zoom adaptativo). */
	private ocrExtraDocumentZoom = 1;
	/** Contador de frames seguidos sin bbox de documento (para el hold antes de zoom-out). */
	private ocrNoDocumentFrames = 0;
	/**
	 * Bbox del documento escalado virtualmente por `ocrExtraDocumentZoom`.
	 * Se usa para validación de bordes (borde azul) y para detectar desbordes del marco externo
	 * cuando el zoom extra es alto pero el bbox del sensor sigue siendo pequeño.
	 */
	private validationDocumentRectOriginal: IPoint | null = null;
	/** Igual que `validationDocumentRectOriginal` pero con margen interno (validaciones de lado). */
	private validationDocumentRect: IPoint | null = null;
	private validationDocumentCenter: IPoint | null = null;
	/** Última zona clasificada; útil para telemetría. */
	private lastZoomZone: DocumentZoomZone = 'none';

	/**
	 * Calcula guías y banda visible sobre el buffer del sensor (`canvas.width` × `canvas.height`).
	 * @param viewportAspect Aspecto del preview en pantalla (p. ej. `video.getBoundingClientRect()`).
	 * Si se omite, se usa el layout del canvas en DOM; si no hay layout, el buffer completo.
	 */
	public setControlRect(canvas: HTMLCanvasElement, viewportAspect?: number): void {
		if (!canvas) {
			return;
		}

		const canvasWidth = canvas.width;
		const canvasHeight = canvas.height;

		const canvasAspectRatio = canvasWidth / canvasHeight;
		const viewportAspectRatio = this.resolveViewportAspectRatio(canvas, viewportAspect);

		if (canvasAspectRatio > viewportAspectRatio) {
			// El canvas es más ancho que el viewport - se recortan los laterales
			const visibleWidth = canvasHeight * viewportAspectRatio;
			const horizontalCrop = (canvasWidth - visibleWidth) / 2;

			this.visibleAreaOffset = {
				left: horizontalCrop,
				right: canvasWidth - horizontalCrop,
				top: 0,
				bottom: canvasHeight,
			};

			this.canvasWidth = visibleWidth;
			this.canvasHeight = canvasHeight;
		} else {
			// El canvas es más alto que el viewport - banda visible centrada en Y (alineada con cover center)
			const visibleHeight = canvasWidth / viewportAspectRatio;
			const verticalCrop = (canvasHeight - visibleHeight) / 2;

			this.visibleAreaOffset = {
				left: 0,
				right: canvasWidth,
				top: verticalCrop,
				bottom: verticalCrop + visibleHeight,
			};

			this.canvasWidth = canvasWidth;
			this.canvasHeight = visibleHeight;
		}

		// EXTERNAL — marco morado en UI; límite exterior de la zona de encuadre.
		const xExternal = this.canvasWidth * this.controlRectPercentage.extHorizontally;
		const yExternal = this.canvasHeight * this.controlRectPercentage.extVertically;

		this.externalControlRect = {
			x: this.visibleAreaOffset.left + xExternal,
			y: this.visibleAreaOffset.top + yExternal,
			width: this.canvasWidth - xExternal * 2,
			height: this.canvasHeight - yExternal * 2,
		};

		// INTERNAL — marco azul en UI; tamaño objetivo del documento bien encuadrado.
		const widthInternal = this.canvasWidth * (1 - this.controlRectPercentage.intHorizontally * 2);
		const heightInternal = this.canvasHeight * (1 - this.controlRectPercentage.intVertically * 2);

		this.controlRectInt = {
			x: this.visibleAreaOffset.left + this.canvasWidth / 2 - widthInternal / 2,
			y: this.visibleAreaOffset.top + this.canvasHeight / 2 - heightInternal / 2,
			width: widthInternal,
			height: heightInternal,
		};
	}

	public drawControl(ctx: CanvasRenderingContext2D): void {
		ctx.strokeStyle = this.draw ? 'green' : 'transparent';
		ctx.lineWidth = 2;
		if (this.externalControlRect) {
			ctx.strokeRect(
				this.externalControlRect.x,
				this.externalControlRect.y,
				this.externalControlRect.width,
				this.externalControlRect.height,
			);
		}
		if (this.controlRectInt) {
			ctx.strokeRect(
				this.controlRectInt.x,
				this.controlRectInt.y,
				this.controlRectInt.width,
				this.controlRectInt.height,
			);
		}
		if (this.documentCenter) {
			ctx.strokeRect(
				this.documentCenter.x,
				this.documentCenter.y,
				this.documentCenter.width,
				this.documentCenter.height,
			);
		}
		if (this.documentRect) {
			ctx.strokeRect(this.documentRect.x, this.documentRect.y, this.documentRect.width, this.documentRect.height);
		}
	}

	/** Categorías de documento aceptadas por el detector MediaPipe. */
	private isDocumentCategoryName(categoryName: string | undefined): boolean {
		if (!categoryName) {
			return false;
		}
		return categoryName === MODEL_DOCUMENT_CATEGORY.DOCUMENT || categoryName === MODEL_DOCUMENT_CATEGORY.PASSPORT;
	}

	public hasDocumentCategory(detections: Detection[]): boolean {
		return Boolean(
			detections?.find(
				(d: Detection) => d.categories?.length && this.isDocumentCategoryName(d.categories[0].categoryName),
			),
		);
	}

	/**
	 * Devuelve la detección document/passport con mayor score del frame.
	 * `find` devolvía la primera categoría y podía aplicar encuadre estricto a pasaportes (NIF-575).
	 */
	public getDocumentDetection(detections: Detection[]): IDocumentDetection {
		if (!detections?.length) return null;

		let bestDetection: Detection | null = null;
		let bestScore = -1;

		for (const detection of detections) {
			const category = detection.categories?.[0];
			if (!category || !this.isDocumentCategoryName(category.categoryName)) {
				continue;
			}
			if (category.score > bestScore) {
				bestScore = category.score;
				bestDetection = detection;
			}
		}

		return bestDetection
			? {
					name: bestDetection.categories[0].categoryName as MODEL_DOCUMENT_CATEGORY,
					score: bestDetection.categories[0].score,
					boundingBox: bestDetection.boundingBox,
				}
			: null;
	}

	/** Detección FACE con mayor score (no se usa para encuadre de documento). */
	public getFaceDetection(detections: Detection[] | undefined): IDocumentDetection | null {
		if (!detections?.length) return null;

		let bestDetection: Detection | null = null;
		let bestScore = -1;

		for (const detection of detections) {
			const category = detection.categories?.[0];
			if (!category || category.categoryName !== MODEL_DOCUMENT_CATEGORY.FACE) {
				continue;
			}
			if (category.score > bestScore) {
				bestScore = category.score;
				bestDetection = detection;
			}
		}

		return bestDetection
			? {
					name: MODEL_DOCUMENT_CATEGORY.FACE,
					score: bestDetection.categories[0].score,
					boundingBox: bestDetection.boundingBox,
				}
			: null;
	}

	/**
	 * Remapea un bbox MediaPipe (coords canvas/vídeo) al JPEG recortado (0–1).
	 * Si el recorte no intersecta, devuelve null.
	 */
	public remapFaceBboxToJpeg(
		boundingBox: BoundingBox | undefined,
		crop: { sx: number; sy: number; sw: number; sh: number },
	): IFaceBboxNorm | null {
		if (!boundingBox || crop.sw <= 0 || crop.sh <= 0) {
			return null;
		}

		const left = this.clamp01((boundingBox.originX - crop.sx) / crop.sw);
		const top = this.clamp01((boundingBox.originY - crop.sy) / crop.sh);
		const right = this.clamp01((boundingBox.originX + boundingBox.width - crop.sx) / crop.sw);
		const bottom = this.clamp01((boundingBox.originY + boundingBox.height - crop.sy) / crop.sh);

		if (left >= right || top >= bottom) {
			return null;
		}

		return { left, top, right, bottom };
	}

	private clamp01(value: number): number {
		if (Number.isNaN(value)) {
			return 0;
		}
		return Math.min(1, Math.max(0, value));
	}

	/**
	 * Encuadre relajado: al menos 3 de 4 lados válidos y documento horizontal.
	 * Permite que un borde sobresalga del marco (caso habitual en pasaportes con altura sobrestimada).
	 */
	public isMatch(requireHorizontalForm = true): boolean {
		const sidesOk =
			[
				this.isDocumentRightSideValid(),
				this.isDocumentLeftSideValid(),
				this.isDocumentTopSideValid(),
				this.isDocumentBottomSideValid(),
			].filter(Boolean).length >= 3;
		return sidesOk && (!requireHorizontalForm || this.isFormDocumentValid());
	}

	/**
	 * Encuadre estricto: los 4 lados dentro del marco, forma horizontal y centro en rectángulo interno.
	 */
	public isMatchStrict(): boolean {
		return (
			this.isDocumentRightSideValid() &&
			this.isDocumentLeftSideValid() &&
			this.isDocumentTopSideValid() &&
			this.isDocumentBottomSideValid() &&
			this.isFormDocumentValid() &&
			!!this.validationDocumentCenter &&
			this.isPointInRect(this.validationDocumentCenter, this.controlRectInt)
		);
	}

	/**
	 * Decide si el frame actual cuenta como documento bien encuadrado según su categoría MediaPipe.
	 * - `passport` → {@link isMatch} (relajado 3/4 lados).
	 * - `document` → {@link isMatchStrict} (4/4 lados + centro).
	 */
	public isFrameMatch(category: MODEL_DOCUMENT_CATEGORY | null): boolean {
		if (!this.validationDocumentRect || !category) {
			return false;
		}
		if (category === MODEL_DOCUMENT_CATEGORY.PASSPORT) {
			// Pasaporte en vertical: no exigir width >= height (NIF-575).
			return this.isMatch(false);
		}
		return this.isMatchStrict();
	}

	/**
	 * Desglose de validaciones de encuadre para depuración y telemetría OCR.
	 * @param category Categoría del mejor `detection`; si se indica, calcula `frameMatch` (criterio real de `isDetected`).
	 */
	public getStrictValidationDiagnostics(category: MODEL_DOCUMENT_CATEGORY | null = null): {
		rightSideValid: boolean;
		leftSideValid: boolean;
		topSideValid: boolean;
		bottomSideValid: boolean;
		formValid: boolean;
		centerInRect: boolean;
		documentInsideExternalRect: boolean;
		documentInsideInternalRect: boolean;
		documentCenterInsideExternalRect: boolean;
		relaxedMatch: boolean;
		strictMatch: boolean;
		frameMatch: boolean;
		ocrExtraDocumentZoom: number;
		documentZoomZone: DocumentZoomZone;
		rects: {
			externalControlRect: IPoint | null;
			internalControlRect: IPoint | null;
			documentRectOriginal: IPoint | null;
			documentRect: IPoint | null;
			documentCenter: IPoint | null;
		};
	} {
		const rightSideValid = !!this.validationDocumentRect && this.isDocumentRightSideValid();
		const leftSideValid = !!this.validationDocumentRect && this.isDocumentLeftSideValid();
		const topSideValid = !!this.validationDocumentRect && this.isDocumentTopSideValid();
		const bottomSideValid = !!this.validationDocumentRect && this.isDocumentBottomSideValid();
		const formValid = !!this.validationDocumentRect && this.isFormDocumentValid();
		const centerInRect =
			!!this.validationDocumentCenter &&
			!!this.controlRectInt &&
			this.isPointInRect(this.validationDocumentCenter, this.controlRectInt);
		const documentInsideExternalRect =
			!!this.validationDocumentRectOriginal &&
			!!this.externalControlRect &&
			this.isRectInsideRect(this.validationDocumentRectOriginal, this.externalControlRect);
		const documentInsideInternalRect =
			!!this.validationDocumentRectOriginal &&
			!!this.controlRectInt &&
			this.isRectInsideRect(this.validationDocumentRectOriginal, this.controlRectInt);
		const documentCenterInsideExternalRect =
			!!this.validationDocumentCenter &&
			!!this.externalControlRect &&
			this.isPointInRect(this.validationDocumentCenter, this.externalControlRect);
		// Siempre se calculan ambos modos para comparar en logs; solo uno aplica según categoría.
		const relaxedMatch =
			[rightSideValid, leftSideValid, topSideValid, bottomSideValid].filter(Boolean).length >= 3 && formValid;
		const strictMatch =
			rightSideValid && leftSideValid && topSideValid && bottomSideValid && formValid && centerInRect;
		const frameMatch = this.isFrameMatch(category);

		return {
			rightSideValid,
			leftSideValid,
			topSideValid,
			bottomSideValid,
			formValid,
			centerInRect,
			documentInsideExternalRect,
			documentInsideInternalRect,
			documentCenterInsideExternalRect,
			relaxedMatch,
			strictMatch,
			frameMatch,
			ocrExtraDocumentZoom: this.ocrExtraDocumentZoom,
			documentZoomZone: this.getDocumentZoomZone(),
			rects: {
				externalControlRect: this.externalControlRect ?? null,
				internalControlRect: this.controlRectInt ?? null,
				documentRectOriginal: this.documentRectOriginal ?? null,
				documentRect: this.documentRect ?? null,
				documentCenter: this.documentCenter ?? null,
			},
		};
	}

	private isRectInsideRect(rect: IPoint, container: IPoint): boolean {
		const rectRight = rect.x + rect.width;
		const rectBottom = rect.y + rect.height;
		const containerRight = container.x + container.width;
		const containerBottom = container.y + container.height;
		return (
			rect.x >= container.x &&
			rect.y >= container.y &&
			rectRight <= containerRight &&
			rectBottom <= containerBottom
		);
	}

	private isFormDocumentValid(): boolean {
		return this.validationDocumentRect!.width >= this.validationDocumentRect!.height;
	}

	private isDocumentRightSideValid(): boolean {
		// Usar el rect original (sin margen) para verificar si está pegado al borde
		if (!this.validationDocumentRectOriginal) return false;

		const leftEdgeOriginal = this.validationDocumentRectOriginal.x;
		const visibleLeft = this.visibleAreaOffset.left;
		const edgeThreshold = this.canvasWidth * this.thresholdDistanceToEdge;

		// Verificar si el documento está pegado al borde izquierdo del área visible (se está saliendo)
		if (leftEdgeOriginal <= visibleLeft + edgeThreshold) return false;

		// Usar el rect con margen para las demás validaciones
		const leftEdge = this.validationDocumentRect!.x;

		// Verificar que el borde izquierdo esté entre los rectángulos de control
		return leftEdge < this.controlRectInt.x && leftEdge > this.externalControlRect.x;
	}

	private isDocumentLeftSideValid(): boolean {
		// Usar el rect original (sin margen) para verificar si está pegado al borde
		if (!this.validationDocumentRectOriginal) return false;

		const rightEdgeOriginal =
			this.validationDocumentRectOriginal.x + this.validationDocumentRectOriginal.width;
		const visibleRight = this.visibleAreaOffset.right;
		const edgeThreshold = this.canvasWidth * this.thresholdDistanceToEdge;

		// Verificar si el documento está pegado al borde derecho del área visible (se está saliendo)
		if (rightEdgeOriginal >= visibleRight - edgeThreshold) return false;

		// Usar el rect con margen para las demás validaciones
		const rightEdge = this.validationDocumentRect!.x + this.validationDocumentRect!.width;

		// Verificar que el borde derecho esté entre los rectángulos de control
		return (
			rightEdge > this.controlRectInt.x + this.controlRectInt.width &&
			rightEdge < this.externalControlRect.x + this.externalControlRect.width
		);
	}

	private isDocumentTopSideValid(): boolean {
		// Usar el rect original (sin margen) para verificar si está pegado al borde
		if (!this.validationDocumentRectOriginal) return false;

		const topEdgeOriginal = this.validationDocumentRectOriginal.y;
		const visibleTop = this.visibleAreaOffset.top;
		const edgeThreshold = this.canvasHeight * this.thresholdDistanceToEdge;

		// Verificar si el documento está pegado al borde superior del área visible (se está saliendo)
		if (topEdgeOriginal <= visibleTop + edgeThreshold) return false;

		// Usar el rect con margen para las demás validaciones
		const topEdge = this.validationDocumentRect!.y;

		// En móvil, solo verificar límite del canvas (ya verificado arriba)
		if (this.isMobile) {
			const isValid = topEdge < this.controlRectInt.y;
			return isValid;
		}

		// En desktop, verificar que esté entre los rectángulos de control
		const isValid = topEdge < this.controlRectInt.y && topEdge > this.externalControlRect.y;
		return isValid;
	}

	private isDocumentBottomSideValid(): boolean {
		// Usar el rect original (sin margen) para verificar si está pegado al borde
		if (!this.validationDocumentRectOriginal) return false;

		const bottomEdgeOriginal =
			this.validationDocumentRectOriginal.y + this.validationDocumentRectOriginal.height;
		const visibleBottom = this.visibleAreaOffset.bottom;
		const edgeThreshold = this.canvasHeight * this.thresholdDistanceToEdge;

		// Verificar si el documento está pegado al borde inferior del área visible (se está saliendo)
		if (bottomEdgeOriginal >= visibleBottom - edgeThreshold) return false;

		// Usar el rect con margen para las demás validaciones
		const bottomEdge = this.validationDocumentRect!.y + this.validationDocumentRect!.height;
		const internalBottom = this.controlRectInt.y + this.controlRectInt.height;
		const externalBottom = this.externalControlRect.y + this.externalControlRect.height;

		// En móvil, solo verificar límite del canvas (ya verificado arriba)
		if (this.isMobile) {
			const isValid = bottomEdge > internalBottom;
			return isValid;
		}

		// En desktop, verificar que esté entre los rectángulos de control
		const isValid = bottomEdge > internalBottom && bottomEdge < externalBottom;
		return isValid;
	}

	private isDocumentAlignedHorizontally(): boolean {
		return (
			this.documentRect.x < this.controlRectInt.x &&
			this.documentRect.x > this.externalControlRect.x &&
			this.documentRect.x + this.documentRect.width > this.controlRectInt.x + this.controlRectInt.width &&
			this.documentRect.x + this.documentRect.width < this.externalControlRect.x + this.externalControlRect.width
		);
	}

	private isDocumentAlignedVertically(): boolean {
		return (
			this.documentRect.y < this.controlRectInt.y &&
			this.documentRect.y > this.externalControlRect.y &&
			this.documentRect.y + this.documentRect.height > this.controlRectInt.y + this.controlRectInt.height &&
			this.documentRect.y + this.documentRect.height <
				this.externalControlRect.y + this.externalControlRect.height
		);
	}

	public setDocumentPoints(rect: IPoint, canvas?: HTMLCanvasElement): void {
		// Rect del sensor sin margen: fuente de verdad para zoom y comprobación de bordes visibles.
		this.documentRectOriginal = rect;
		// Rect con margen interno (~3 %): validaciones de lado y borde azul (`isFrameMatch`).
		this.documentRect = this.updateSize(rect, canvas);
		if (rect) {
			this.documentCenter = {
				x: rect.x + rect.width / 2,
				y: rect.y + rect.height / 2,
				height: 1,
				width: 1,
			};
		} else {
			this.documentCenter = null;
		}
		this.refreshValidationDocumentRects();
	}

	private updateSize(rect: IPoint, canvas?: HTMLCanvasElement): IPoint {
		if (rect && canvas) {
			const margin: number = 0.03;
			// Usar las dimensiones guardadas para mantener consistencia con las validaciones
			const width = this.canvasWidth || canvas.width;
			const height = this.canvasHeight || canvas.height;
			return {
				x: rect.x + width * margin,
				y: rect.y + height * margin,
				width: rect.width - width * margin * 2,
				height: rect.height - height * margin * 2,
			};
		}
		return rect;
	}

	public isPointInRect(p: IPoint, r: IPoint): boolean {
		return p?.x >= r?.x && p?.x <= r?.x + r?.width && p?.y >= r?.y && p?.y <= r?.y + r?.height;
	}

	public isPointAboveRect(p: IPoint, r: IPoint): boolean {
		return p?.y < r?.y;
	}

	public isPointBelowRect(p: IPoint, r: IPoint): boolean {
		return p?.y > r?.y + r?.height;
	}

	public isPointRightRect(p: IPoint, r: IPoint): boolean {
		return p?.x < r?.x;
	}

	public isPointLeftRect(p: IPoint, r: IPoint): boolean {
		return p?.x > r?.x + r?.width;
	}

	// Draw
	public getDraw(): boolean {
		return this.draw;
	}

	public setDraw(value: boolean): void {
		this.draw = value;
	}

	public getExternalControlRect(): IPoint {
		return this.externalControlRect;
	}

	/**
	 * Recorte del buffer del sensor equivalente a la banda visible del canvas de análisis.
	 * Requiere `setControlRect(canvas)` previo con las dimensiones del vídeo.
	 */
	private resolveViewportAspectRatio(canvas: HTMLCanvasElement, viewportAspect?: number): number {
		if (viewportAspect != null && viewportAspect > 0) {
			return viewportAspect;
		}
		const rect = canvas.getBoundingClientRect();
		if (rect.width > 0 && rect.height > 0) {
			return rect.width / rect.height;
		}
		if (canvas.width > 0 && canvas.height > 0) {
			return canvas.width / canvas.height;
		}
		return 1;
	}

	public getVisibleAnalysisSourceRect(): VideoCoverSourceRect | null {
		const { left, right, top, bottom } = this.visibleAreaOffset;
		const sw = right - left;
		const sh = bottom - top;
		if (sw <= 0 || sh <= 0) {
			return null;
		}
		return { sx: left, sy: top, sw, sh };
	}

	/** Centro del marco guía OCR en coordenadas normalizadas del sensor. */
	public getGuideFocusPointNormalized(videoWidth: number, videoHeight: number): { x: number; y: number } | null {
		if (!videoWidth || !videoHeight || !this.externalControlRect) return null;
		const centerX = this.externalControlRect.x + this.externalControlRect.width / 2;
		const centerY = this.externalControlRect.y + this.externalControlRect.height / 2;
		return {
			x: Math.min(1, Math.max(0, centerX / videoWidth)),
			y: Math.min(1, Math.max(0, centerY / videoHeight)),
		};
	}

	/** Centro del último documento detectado en coordenadas normalizadas del sensor. */
	public getDocumentFocusPointNormalized(videoWidth: number, videoHeight: number): { x: number; y: number } | null {
		if (!videoWidth || !videoHeight || !this.documentCenter) return null;
		return {
			x: Math.min(1, Math.max(0, this.documentCenter.x / videoWidth)),
			y: Math.min(1, Math.max(0, this.documentCenter.y / videoHeight)),
		};
	}

	// Mobile
	public setIsMobile(isMobile: boolean): void {
		this.isMobile = isMobile;
	}

	// Zoom ────────────────────────────────────────────────────────────────────────

	/**
	 * Zoom guía fijo del preview: escala para que el marco externo (morado) ocupe el área visible.
	 * No cambia frame a frame; lo complementa `ocrExtraDocumentZoom`.
	 */
	public getZoomFactor(): number {
		if (!this.externalControlRect || !this.canvasWidth || !this.canvasHeight) {
			return 1;
		}
		// Factor de zoom para que el externalControlRect ocupe todo el espacio visible
		const zoomX = this.canvasWidth / this.externalControlRect.width;
		const zoomY = this.canvasHeight / this.externalControlRect.height;
		return Math.min(zoomX, zoomY);
	}

	public getZoomOffsetX(): number {
		const zoomFactorHalf = this.getZoomFactor();
		return (this.canvasWidth - this.canvasWidth * zoomFactorHalf) / 4;
	}

	public getZoomOffsetY(): number {
		const zoomFactorHalf = this.getZoomFactor();
		return (this.canvasHeight - this.canvasHeight * zoomFactorHalf) / 4;
	}

	/** Multiplicador extra adaptativo (1 = neutro). Lo ajusta `updateDocumentFitZoom`. */
	public getOcrExtraDocumentZoom(): number {
		return this.ocrExtraDocumentZoom;
	}

	/** Tope de zoom extra según dispositivo. */
	public getOcrExtraZoomMax(): number {
		return this.isMobile ? OCR_EXTRA_ZOOM_MAX_MOBILE : OCR_EXTRA_ZOOM_MAX_DESKTOP;
	}

	/** Zoom total del preview en vivo: guía × extra. El JPEG de captura hornea este factor. */
	public getEffectivePreviewZoom(): number {
		return this.getZoomFactor() * this.ocrExtraDocumentZoom;
	}

	/** Restaura zoom extra y estado de zona (cambio de cámara, rotar dispositivo, reinicio OCR). */
	public resetDocumentFitZoom(): void {
		this.ocrExtraDocumentZoom = 1;
		this.lastZoomZone = 'none';
		this.ocrNoDocumentFrames = 0;
		this.refreshValidationDocumentRects();
	}

	/**
	 * Ajuste bidireccional del zoom extra según el tamaño del documento vs marcos guía.
	 *
	 * Flujo por frame (lo invoca `ocr.component` tras `setDocumentPoints` + `setControlRect`):
	 * 1. Sin documento → tras `OCR_NO_DOCUMENT_HOLD_FRAMES`, rampa suave hacia extra = 1.
	 * 2. Bbox ignorado (falso positivo pantalla llena) → no tocar zoom.
	 * 3. Clasificar zona → `classifyZoomZone`.
	 * 4. `safe` → congelar extra; `too_small` / `too_large` → acercar extra al target con rampa.
	 */
	public updateDocumentFitZoom(hasDocument: boolean, category: MODEL_DOCUMENT_CATEGORY | null = null): void {
		// ── Sin detección: mantener zoom unos frames y luego volver a 1 ──
		if (!hasDocument || !this.documentRectOriginal || !this.controlRectInt || !this.externalControlRect) {
			this.lastZoomZone = 'none';
			this.ocrNoDocumentFrames++;
			if (
				this.ocrNoDocumentFrames <= OCR_NO_DOCUMENT_HOLD_FRAMES &&
				this.ocrExtraDocumentZoom !== 1
			) {
				return;
			}
			if (this.ocrExtraDocumentZoom !== 1) {
				this.ocrExtraDocumentZoom = this.applyExtraZoomRamp(this.ocrExtraDocumentZoom, 1);
				this.refreshValidationDocumentRects();
			}
			return;
		}

		this.ocrNoDocumentFrames = 0;

		// ── Filtro anti falsos positivos (pantalla casi llena sin documento real) ──
		if (!this.isBboxUsableForZoom(this.documentRectOriginal, this.externalControlRect)) {
			this.lastZoomZone = 'none';
			return;
		}

		const zone = this.classifyZoomZone(category);
		this.lastZoomZone = zone;
		const target = this.computeZoomTarget(zone);

		// En banda segura no movemos el extra (aunque el target coincida con el actual).
		if (zone === 'safe') {
			this.refreshValidationDocumentRects();
			return;
		}
		this.ocrExtraDocumentZoom = this.applyExtraZoomRamp(this.ocrExtraDocumentZoom, target);
		this.refreshValidationDocumentRects();
	}

	/** Zona actual sin modificar estado; usada en telemetría y tests. */
	public getDocumentZoomZone(category: MODEL_DOCUMENT_CATEGORY | null = null): DocumentZoomZone {
		if (!this.documentRectOriginal || !this.externalControlRect || !this.controlRectInt) {
			return 'none';
		}
		return this.classifyZoomZone(category);
	}

	// ── Clasificación de zona (too_small / safe / too_large) ────────────────────
	//
	// Comparación por dimensiones del bbox del sensor (`documentRectOriginal`), no por posición
	// centrada. La posición la valida aparte `isFrameMatch` (borde azul).
	//
	//   too_large  → sale del marco morado O ancho/alto > marco externo
	//   too_small  → ancho < marco interno O alto < marco interno
	//   safe       → ancho/alto ≥ interno Y ≤ externo (banda donde encajaría el borde azul/morado)

	/**
	 * Audio «demasiado cerca» (`IS_TOO_CLOSE`): umbral blando al 92 % del externo.
	 * Independiente de la clasificación de zoom (esa usa `isDocumentLargerThanExternal`).
	 */
	public isDocumentTooClose(bboxWidth: number, bboxHeight: number): boolean {
		const ext = this.externalControlRect;
		if (!ext || !bboxWidth || !bboxHeight) {
			return false;
		}
		return Math.max(bboxWidth / ext.width, bboxHeight / ext.height) > OCR_TOO_CLOSE_FILL_RATIO;
	}

	/** Alguna dimensión del bbox supera estrictamente el marco externo (morado). */
	private isDocumentLargerThanExternal(doc: IPoint, ext: IPoint): boolean {
		return doc.width > ext.width || doc.height > ext.height;
	}

	/** Alguna dimensión del bbox queda por debajo del marco interno (azul). */
	private isDocumentSmallerThanInternal(doc: IPoint, intRect: IPoint): boolean {
		return doc.width < intRect.width || doc.height < intRect.height;
	}

	/**
	 * Banda segura de tamaño: el documento, si estuviera centrado, mostraría borde azul/morado.
	 * Requiere cumplir las cuatro desigualdades a la vez (ancho y alto en rango [interno, externo]).
	 */
	private isDocumentInSafeSizeBand(doc: IPoint, intRect: IPoint, ext: IPoint): boolean {
		return (
			doc.width >= intRect.width &&
			doc.height >= intRect.height &&
			doc.width <= ext.width &&
			doc.height <= ext.height
		);
	}

	/** Algún borde del rectángulo sale del marco externo (comprobación espacial, no solo tamaño). */
	private isDocumentOutsideExternalRect(doc: IPoint, ext: IPoint): boolean {
		return !this.isRectInsideRect(doc, ext);
	}

	/**
	 * Decide la zona de zoom comparando el bbox del sensor con los marcos interno/externo.
	 *
	 * Orden de evaluación (importante):
	 * 1. Desborde espacial o tamaño > externo → `too_large` (alejar zoom).
	 * 2. Tamaño < interno → `too_small` (acercar zoom), salvo excepción en tope de extra.
	 * 3. Tamaño en banda [interno, externo] → `safe` (no mover zoom).
	 *
	 * Excepción en tope de extra: si el sensor sigue siendo pequeño pero ya no podemos
	 * hacer más zoom-in, congelamos en `safe` salvo que el bbox *efectivo* (sensor × extra)
	 * se salga del marco externo → entonces `too_large` para bajar extra.
	 */
	private classifyZoomZone(category: MODEL_DOCUMENT_CATEGORY | null = null): DocumentZoomZone {
		const doc = this.documentRectOriginal!;
		const ext = this.externalControlRect!;
		const intRect = this.controlRectInt!;

		this.refreshValidationDocumentRects();
		const effectiveDoc = this.getEffectiveDocumentRectForZoom();

		// 1) Demasiado grande o fuera del marco morado
		if (this.isDocumentOutsideExternalRect(doc, ext)) {
			return 'too_large';
		}

		if (this.isDocumentLargerThanExternal(doc, ext)) {
			return 'too_large';
		}

		const maxZoom = this.isMobile ? OCR_EXTRA_ZOOM_MAX_MOBILE : OCR_EXTRA_ZOOM_MAX_DESKTOP;
		const atMaxExtra = this.ocrExtraDocumentZoom >= maxZoom - 0.005;

		// 2) Demasiado pequeño → zoom-in (o congelar / corregir en tope)
		if (this.isDocumentSmallerThanInternal(doc, intRect)) {
			if (atMaxExtra) {
				// Zoom virtual puede hacer que el bbox efectivo sobresalga aunque el sensor sea pequeño
				return this.isDocumentOutsideExternalRect(effectiveDoc, ext) ? 'too_large' : 'safe';
			}
			return 'too_small';
		}

		// 3) Dimensiones en banda objetivo
		if (this.isDocumentInSafeSizeBand(doc, intRect, ext)) {
			return 'safe';
		}

		// Tras los pasos anteriores, si no es too_small ni too_large, está en banda por construcción
		return 'safe';
	}

	/**
	 * Bbox del documento tras aplicar el zoom extra virtual (mismo criterio que borde azul).
	 * El sensor (`documentRectOriginal`) no cambia; este rect sí escala con `ocrExtraDocumentZoom`.
	 */
	private getEffectiveDocumentRectForZoom(): IPoint {
		return (
			this.validationDocumentRectOriginal ??
			this.documentRectOriginal ?? { x: 0, y: 0, width: 0, height: 0 }
		);
	}

	/**
	 * Cuánto falta de zoom-in para llenar el marco interno en el eje más restrictivo.
	 * Solo alimenta la rampa (`computeZoomTarget`); NO clasifica la zona.
	 *
	 * Ejemplo: rawIn = 1.2 → haría falta ~20 % más de zoom extra para encajar el interno.
	 * Se multiplica por `OCR_DOCUMENT_FIT_MARGIN` (0.88) para no pasarse al acercar.
	 */
	private computeRawZoomInForDoc(doc: IPoint): number {
		if (!this.controlRectInt) {
			return 1;
		}
		const intRect = this.controlRectInt;
		return Math.min(intRect.width / doc.width, intRect.height / doc.height) * OCR_DOCUMENT_FIT_MARGIN;
	}

	private computeRawZoomIn(): number {
		if (!this.documentRectOriginal) {
			return 1;
		}
		return this.computeRawZoomInForDoc(this.documentRectOriginal);
	}

	/**
	 * Calcula el valor objetivo de `ocrExtraDocumentZoom` según la zona.
	 * La rampa real la aplica `applyExtraZoomRamp` (pasos pequeños por frame).
	 */
	private computeZoomTarget(zone: DocumentZoomZone): number {
		const doc = this.documentRectOriginal!;
		const ext = this.externalControlRect!;
		const current = this.ocrExtraDocumentZoom;
		const maxZoom = this.isMobile ? OCR_EXTRA_ZOOM_MAX_MOBILE : OCR_EXTRA_ZOOM_MAX_DESKTOP;

		if (zone === 'safe') {
			return current;
		}

		if (zone === 'too_small') {
			// Subida proporcional: no saltar directo al tope
			const rawIn = this.computeRawZoomIn();
			return Math.min(maxZoom, current * rawIn);
		}

		if (zone === 'too_large') {
			const effectiveDoc = this.getEffectiveDocumentRectForZoom();

			// Bbox efectivo (zoom virtual) desborda el marco morado
			if (this.isDocumentOutsideExternalRect(effectiveDoc, ext)) {
				const fitScale = Math.min(ext.width / effectiveDoc.width, ext.height / effectiveDoc.height, 1);
				return Math.max(OCR_EXTRA_ZOOM_MIN, current * fitScale);
			}

			// Bbox del sensor desborda espacialmente
			if (this.isDocumentOutsideExternalRect(doc, ext)) {
				const spatialOut = Math.min(ext.width / doc.width, ext.height / doc.height);
				return Math.max(OCR_EXTRA_ZOOM_MIN, Math.min(spatialOut, current));
			}

			// Demasiado grande en tamaño pero aún dentro del externo
			const rawIn = this.computeRawZoomIn();
			const targetZoom = Math.max(OCR_EXTRA_ZOOM_MIN, Math.min(1, current * rawIn));
			return Math.min(targetZoom, current);
		}

		return 1;
	}

	/** Avanza el extra hacia el target un paso fijo por frame (evita saltos bruscos). */
	private applyExtraZoomRamp(current: number, target: number): number {
		if (Math.abs(target - current) < 0.0005) {
			return target;
		}
		const step = this.getZoomRampStep();
		if (target > current) {
			return Math.min(target, current + step);
		}
		return Math.max(target, current - step);
	}

	private getZoomRampStep(): number {
		return this.isMobile ? OCR_EXTRA_ZOOM_RAMP_STEP_MOBILE : OCR_EXTRA_ZOOM_RAMP_STEP_DESKTOP;
	}

	/**
	 * Filtra detecciones espurias antes de mover el zoom.
	 * Si el bbox ocupa casi todo el marco externo (>92 % área) pero NO es más pequeño que
	 * el interno, suele ser un falso positivo de pantalla → ignorar y no hacer zoom-in.
	 */
	private isBboxUsableForZoom(doc: IPoint, ext: IPoint): boolean {
		if (this.isDocumentOutsideExternalRect(doc, ext)) {
			return true;
		}
		this.refreshValidationDocumentRects();
		const effectiveDoc = this.getEffectiveDocumentRectForZoom();
		if (this.isDocumentOutsideExternalRect(effectiveDoc, ext)) {
			return true;
		}
		const areaRatio = (doc.width * doc.height) / (ext.width * ext.height);
		if (areaRatio <= 0.92) {
			return true;
		}
		const intRect = this.controlRectInt;
		if (!intRect) {
			return true;
		}
		return this.isDocumentSmallerThanInternal(doc, intRect);
	}

	/**
	 * Recalcula los rects de validación escalando el bbox según `ocrExtraDocumentZoom`.
	 * El pivote es el centro del marco externo (mismo punto que el zoom del preview).
	 * Sin esto, el borde azul y la detección de desborde efectivo no coincidirían con el preview.
	 */
	private refreshValidationDocumentRects(): void {
		if (!this.documentRectOriginal || !this.documentRect) {
			this.validationDocumentRectOriginal = this.documentRectOriginal;
			this.validationDocumentRect = this.documentRect;
			this.validationDocumentCenter = this.documentCenter;
			return;
		}
		if (this.ocrExtraDocumentZoom === 1) {
			this.validationDocumentRectOriginal = this.documentRectOriginal;
			this.validationDocumentRect = this.documentRect;
			this.validationDocumentCenter = this.documentCenter;
			return;
		}
		const pivot = this.getZoomPivot();
		this.validationDocumentRectOriginal = this.scaleRectAroundPoint(
			this.documentRectOriginal,
			pivot.x,
			pivot.y,
			this.ocrExtraDocumentZoom,
		);
		this.validationDocumentRect = this.scaleRectAroundPoint(
			this.documentRect,
			pivot.x,
			pivot.y,
			this.ocrExtraDocumentZoom,
		);
		if (this.documentCenter) {
			const scaledCenter = this.scaleRectAroundPoint(
				{ x: this.documentCenter.x, y: this.documentCenter.y, width: 1, height: 1 },
				pivot.x,
				pivot.y,
				this.ocrExtraDocumentZoom,
			);
			this.validationDocumentCenter = {
				...this.documentCenter,
				x: scaledCenter.x,
				y: scaledCenter.y,
			};
		} else {
			this.validationDocumentCenter = null;
		}
	}

	/** Centro del marco externo: origen del zoom extra virtual. */
	private getZoomPivot(): { x: number; y: number } {
		if (!this.externalControlRect) {
			return { x: 0, y: 0 };
		}
		return {
			x: this.externalControlRect.x + this.externalControlRect.width / 2,
			y: this.externalControlRect.y + this.externalControlRect.height / 2,
		};
	}

	/** Escala un rectángulo alrededor de un punto fijo (pivote). factor > 1 agranda; < 1 reduce. */
	private scaleRectAroundPoint(rect: IPoint, pivotX: number, pivotY: number, factor: number): IPoint {
		return {
			x: pivotX + (rect.x - pivotX) * factor,
			y: pivotY + (rect.y - pivotY) * factor,
			width: rect.width * factor,
			height: rect.height * factor,
		};
	}

	/**
	 * Calcula el rectángulo de la franja barcode alineado al overlay SVG (scan-dibujo).
	 * Requiere `setControlRect` previo o se invoca internamente.
	 */
	public setBarcodeStripRect(canvas: HTMLCanvasElement, viewportAspect?: number): IPoint {
		this.setControlRect(canvas, viewportAspect);
		this.barcodeStripRect = {
			x: this.visibleAreaOffset.left + this.canvasWidth * PositionDocumentService.BARCODE_STRIP.left,
			y: this.visibleAreaOffset.top + this.canvasHeight * PositionDocumentService.BARCODE_STRIP.top,
			width: this.canvasWidth * PositionDocumentService.BARCODE_STRIP.width,
			height: this.canvasHeight * PositionDocumentService.BARCODE_STRIP.height,
		};
		return this.barcodeStripRect;
	}

	public getBarcodeStripRect(): IPoint | null {
		return this.barcodeStripRect;
	}

	/**
	 * True cuando el documento detectado (DNI/NIE, no pasaporte) solapa la franja barcode y cumple encuadre estricto.
	 * Tras NIF-293 el modelo usa categoría genérica `document` (antes `document_back` en PRE).
	 */
	public isBarcodeCaptureReady(category: MODEL_DOCUMENT_CATEGORY | null): boolean {
		if (!this.documentRectOriginal || !this.barcodeStripRect || !category) {
			return false;
		}
		if (category !== MODEL_DOCUMENT_CATEGORY.DOCUMENT) {
			return false;
		}
		return this.rectsOverlap(this.documentRectOriginal, this.barcodeStripRect) && this.isFrameMatch(category);
	}

	private rectsOverlap(a: IPoint, b: IPoint): boolean {
		return (
			a.x < b.x + b.width &&
			a.x + a.width > b.x &&
			a.y < b.y + b.height &&
			a.y + a.height > b.y
		);
	}
}
